import { AuditType, IAuditConfig, InformativeCheckType } from '../data/audits';

/** Client-only publish diff audits — not in audit_configs / not profile-managed. */
export const CLIENT_ONLY_CONTENT_DIFF_AUDIT_SLUGS = new Set([
  'CONTENT_DIFF_AUDIT',
  'PUBLISHED_CONTENT_DIFF_AUDIT',
]);

/** Audits that participate in profile assignment, catalog sweep, and Audit Library toggles. */
export const isProfileManagedAudit = (audit: IAuditConfig): boolean =>
  !audit.isContentDiff && !CLIENT_ONLY_CONTENT_DIFF_AUDIT_SLUGS.has(audit.slug);

export const filterProfileManagedAudits = (audits: IAuditConfig[]): IAuditConfig[] =>
  audits.filter(isProfileManagedAudit);

/**
 * Psych-team validation stays assigned under warnings-only defaults (unsaved profile).
 * Other blocking audits remain opt-in via Audits Selection.
 */
export const AUDIT_TYPES_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT: ReadonlySet<AuditType> = new Set(['PSYCH']);

/**
 * Seed audits that stay assigned regardless of audit type. These close a gap a saved profile
 * cannot know about yet, so opting in per profile would leave the gap open on every existing
 * assignment row until an author edits it.
 */
export const AUDIT_SLUGS_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT: ReadonlySet<string> = new Set([
  'FT_PLACEMENT_AUDIT',
]);

export const isExemptFromWarningsOnlyDefault = (audit: IAuditConfig): boolean =>
  AUDIT_SLUGS_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT.has(audit.slug)
  || (!!audit.auditType && AUDIT_TYPES_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT.has(audit.auditType));

export interface ExcludedChecks {
  [checkId: string]: boolean;
}

export interface ExcludedAuditEntry {
  isExcluded: boolean;
  excludedChecks: ExcludedChecks;
  /** Schema 2 marker: every check the profile knew of has an explicit value. Written on every save. */
  checksMaterialized?: boolean;
}

/**
 * Whether a check is off for an audit entry. Selection-only: a check is on only when the entry says
 * `false` for it, so a check added later (no value) stays off until an author selects it.
 */
export const isCheckExcludedInEntry = (
  entry: ExcludedAuditEntry | null | undefined,
  checkId: string,
): boolean => entry?.excludedChecks?.[checkId] !== false;

export type ExcludedAuditsMap = { [slug: string]: ExcludedAuditEntry };

export const getDefaultExcludedAuditEntry = (): ExcludedAuditEntry => {
  return { isExcluded: false, excludedChecks: {} };
};

const isStaticWarningCheck = (
  checkInformative?: InformativeCheckType,
  auditInformative?: InformativeCheckType,
): boolean => {
  const value = checkInformative ?? auditInformative;
  return value !== undefined && value !== false && value !== null;
};

/**
 * Suggested audit profile assignment: enable informative (warning) checks only and exclude
 * blocking audits and sub-checks. Not applied automatically: a profile with no saved assignment
 * has no audits.
 */
export const buildWarningsOnlyDefaultExcludedConfig = (
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  const config: ExcludedAuditsMap = {};

  for (const audit of filterProfileManagedAudits(audits)) {
    if (audit.isDisabled || audit.isHidden) {
      continue;
    }

    if (isExemptFromWarningsOnlyDefault(audit)) {
      // Selection-only model: an audit that stays active must be listed.
      const exemptChecks: ExcludedChecks = {};
      for (const c of audit.checks ?? []) {
        if (!!c.caption && c.id) { exemptChecks[c.id] = false; }
      }
      config[audit.slug] = { isExcluded: false, excludedChecks: exemptChecks, checksMaterialized: true };
      continue;
    }

    const entry: ExcludedAuditEntry = { ...getDefaultExcludedAuditEntry(), checksMaterialized: true };
    const checks = audit.checks?.filter((check) => !!check.caption && check.id) ?? [];

    if (!checks.length) {
      config[audit.slug] = { isExcluded: true, excludedChecks: {}, checksMaterialized: true };
      continue;
    }

    let hasWarningCheck = false;
    for (const check of checks) {
      if (
        isStaticWarningCheck(
          check.informativeOnly,
          audit.commonCheckProps?.informativeOnly,
        )
      ) {
        hasWarningCheck = true;
        entry.excludedChecks[check.id] = false;
      } else {
        entry.excludedChecks[check.id] = true;
      }
    }

    entry.isExcluded = !hasWarningCheck;
    config[audit.slug] = entry;
  }

  return config;
};

/**
 * Selection-only model: the saved map lists the active audits and an audit missing from it is off.
 * There is no fallback: a profile with no saved assignment has no audits (the UI says so), and the
 * legacy `audit_profile_config` table and the warnings-only default are never consulted.
 */
export const resolveProfileSelection = (
  stored: ExcludedAuditsMap | null | undefined,
): ExcludedAuditsMap => {
  return stored && Object.keys(stored).length ? stored : {};
};

export type AuditProfileSelectionState = 'loading' | 'not-defined' | 'none-selected' | 'selected' | 'load-failed' | 'needs-upgrade';

/** Why a profile has (or lacks) audits, for the notice shown instead of an empty list. */
export const getProfileSelectionState = (
  hasSavedRow: boolean,
  selection: ExcludedAuditsMap | null | undefined,
  needsUpgrade = false,
): AuditProfileSelectionState => {
  if (needsUpgrade) { return 'needs-upgrade'; }
  if (!hasSavedRow) { return 'not-defined'; }
  const anyActive = Object.keys(selection ?? {}).some(slug => isAuditActiveInProfile(selection, slug));
  return anyActive ? 'selected' : 'none-selected';
};

export const PROFILE_SELECTION_NOTICES: { [state in AuditProfileSelectionState]: string } = {
  'loading': '',
  'not-defined': 'No audits are defined for this assessment profile, so no audits are available. Select the audits to run in Parameters > Audit Library and save.',
  'none-selected': 'This assessment profile has no audits selected, so no audits are available. Select the audits to run in Parameters > Audit Library and save.',
  'selected': '',
  'needs-upgrade': 'This assessment profile was saved on an older version of the audit configuration, so no audits are run for it. Use "Upgrade all profiles" in the Audit Library (or save the profile again) to bring it to the current version.',
  'load-failed': 'The audit profile could not be loaded, so no audits are available. Reload and try again.',
};

/** Selection-only model: active only when the profile lists the audit and does not exclude it. */
export const isAuditActiveInProfile = (
  config: ExcludedAuditsMap | null | undefined,
  slug: string,
): boolean => {
  const entry = config?.[slug];
  return !!entry && !entry.isExcluded;
};

/**
 * Writes every profile-managed audit into the map (listed as off unless it is already listed), so a
 * saved profile always carries the full selection and the version history shows it.
 */
export const materializeProfileSelection = (
  config: ExcludedAuditsMap | null | undefined,
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  const result: ExcludedAuditsMap = JSON.parse(JSON.stringify(config ?? {}));
  for (const audit of filterProfileManagedAudits(audits)) {
    if (!result[audit.slug]) {
      result[audit.slug] = { isExcluded: true, excludedChecks: {}, checksMaterialized: true };
    }
    const entry = result[audit.slug];
    entry.excludedChecks ||= {};
    // Record the current on/off of every known check (no value = off) so the saved profile is explicit.
    for (const check of audit.checks?.filter((c) => !!c.caption && c.id) ?? []) {
      entry.excludedChecks[check.id] = isCheckExcludedInEntry(entry, check.id);
    }
    entry.checksMaterialized = true;
  }
  return result;
};

/**
 * Saved profile map + catalog sweep: fill missing profile-managed slugs/checks with
 * warnings-only defaults. Client-only publish diff audits are out of scope.
 */
export const mergeStoredConfigWithCatalog = (
  stored: ExcludedAuditsMap,
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  const profileAudits = filterProfileManagedAudits(audits);
  const warningsDefault = buildWarningsOnlyDefaultExcludedConfig(profileAudits);
  const merged: ExcludedAuditsMap = JSON.parse(JSON.stringify(stored));

  for (const audit of profileAudits) {
    const slug = audit.slug;
    const defaultEntry = warningsDefault[slug] ?? getDefaultExcludedAuditEntry();
    const storedEntry = merged[slug];

    if (!storedEntry) {
      // Selection-only model: not selected on this profile, so it stays off.
      merged[slug] = { isExcluded: true, excludedChecks: {} };
      continue;
    }

    if (!storedEntry.excludedChecks) {
      storedEntry.excludedChecks = {};
    }

    for (const check of audit.checks?.filter((c) => !!c.caption && c.id) ?? []) {
      if (storedEntry.excludedChecks[check.id] === undefined) {
        storedEntry.excludedChecks[check.id] = defaultEntry.excludedChecks[check.id] !== false;
      }
    }
  }

  return merged;
};

/** Mirrors public/test-auth/audit-config-impact classifyEnablement (selection-only: missing = excluded). */
export type AuditProfileEnablement = 'enabled' | 'partial' | 'excluded' | 'not-defined';

export const classifyAuditProfileEnablement = (
  config: ExcludedAuditsMap | null | undefined,
  slug: string,
): AuditProfileEnablement => {
  if (!config) { return 'not-defined'; }
  const entry = config[slug];
  if (!entry) { return 'excluded'; }
  if (entry.isExcluded) { return 'excluded'; }
  if (Object.values(entry.excludedChecks ?? {}).some(Boolean)) { return 'partial'; }
  return 'enabled';
};

/**
 * Selecting an audit on a profile: every check with no explicit value is switched on, so selecting the
 * audit does not leave it with nothing to run. A check the author already turned off stays off.
 */
export const selectAuditChecks = (entry: ExcludedAuditEntry, audit: IAuditConfig | undefined): void => {
  entry.excludedChecks ||= {};
  for (const check of audit?.checks?.filter((c) => !!c.caption && c.id) ?? []) {
    if (entry.excludedChecks[check.id] === undefined) { entry.excludedChecks[check.id] = false; }
  }
};

export const isAuditUsedOnProfile = (enablement: AuditProfileEnablement): boolean =>
  enablement !== 'excluded';
